import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import React, { useEffect, useRef } from 'react';
import { standardLegend, standardTitleStyle } from '../../utils/chartFormatting';
import './ChartRendererSupabase.css';

Chart.register(...registerables);

interface ChartRendererSupabaseProps {
  chartData: any;
  chartOptions: any;
  height?: string;
  width?: string;
  title?: string;
  location?: string;
  chartRef?: React.RefObject<HTMLCanvasElement>;
  /** Force remount when tabs change so Chart.js measures a visible container. */
  remountKey?: string;
}

export const ChartRendererSupabase: React.FC<ChartRendererSupabaseProps> = ({
  chartData,
  chartOptions,
  height,
  width = '100%',
  title,
  location,
  chartRef: externalChartRef,
  remountKey,
}) => {
  const internalChartRef = useRef<HTMLCanvasElement>(null as unknown as HTMLCanvasElement);
  const chartRef = (externalChartRef ?? internalChartRef) as React.RefObject<HTMLCanvasElement>;
  const chartInstanceRef = useRef<Chart | null>(null);
  const isDestroyedRef = useRef<boolean>(false);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartRef.current || !chartData || !chartOptions) return;

    if (chartInstanceRef.current && !isDestroyedRef.current) {
      try {
        chartInstanceRef.current.destroy();
        isDestroyedRef.current = true;
      } catch {
        // already destroyed
      }
      chartInstanceRef.current = null;
    }

    isDestroyedRef.current = false;

    const ctx = chartRef.current.getContext('2d');
    if (ctx) {
      try {
        const legendOptions = chartOptions?.plugins?.legend
          ? {
              ...chartOptions.plugins.legend,
              position: standardLegend.position,
              labels: {
                ...standardLegend.labels,
                ...(chartOptions.plugins.legend.labels || {}),
              },
            }
          : chartOptions?.plugins?.legend;

        const titleOptions = chartOptions?.plugins?.title
          ? {
              ...chartOptions.plugins.title,
              ...standardTitleStyle,
              font: {
                ...standardTitleStyle.font,
                ...(chartOptions.plugins.title.font || {}),
              },
              padding: {
                ...standardTitleStyle.padding,
                ...(chartOptions.plugins.title.padding || {}),
              },
            }
          : chartOptions?.plugins?.title;

        const newChart = new Chart(ctx, {
          type: chartOptions.type || 'line',
          data: chartData,
          options: {
            ...chartOptions,
            responsive: chartOptions.responsive ?? true,
            maintainAspectRatio: chartOptions.maintainAspectRatio ?? false,
            plugins: {
              ...(chartOptions.plugins || {}),
              ...(legendOptions ? { legend: legendOptions } : {}),
              ...(titleOptions ? { title: titleOptions } : {}),
            },
            animation: false,
          },
        });

        chartInstanceRef.current = newChart;
        requestAnimationFrame(() => {
          try {
            newChart.resize();
          } catch {
            // ignore
          }
        });
      } catch {
        // Chart creation failed
      }
    }

    return () => {
      if (chartInstanceRef.current && !isDestroyedRef.current) {
        try {
          chartInstanceRef.current.destroy();
          isDestroyedRef.current = true;
        } catch {
          // cleanup failed
        }
        chartInstanceRef.current = null;
      }
    };
  }, [chartData, chartOptions, chartRef, remountKey]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
      if (chartInstanceRef.current && !isDestroyedRef.current) {
        try {
          chartInstanceRef.current.resize();
        } catch {
          // ignore
        }
      }
    });
    ro.observe(host);
    return () => ro.disconnect();
  }, [remountKey]);

  const hostStyle: React.CSSProperties = height
    ? { width, height, position: 'relative', overflow: 'hidden', boxSizing: 'border-box' }
    : { width };

  return (
    <div ref={hostRef} className="cp-chartjs-host" style={hostStyle}>
      <canvas
        ref={chartRef}
        style={{
          display: 'block',
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
};
